import { useLayoutEffect, useMemo } from 'react';
import { PiMoonDuotone, PiSunDimDuotone } from 'react-icons/pi';

import { Translate, useTranslate } from '@spazious/dollhouse-i18n';
import { useStore } from '@spazious/dollhouse-store';
import { ThemeModeEnum, UserSettings } from '@spazious/dollhouse-types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogProps,
  Flex,
  Switch,
  ToggleButton,
  Typography,
} from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

import { UnitsSelector } from '../index.js';
import { LanguageSelector } from '../LanguageSelector/LanguageSelector.js';
import { VersionBanner } from '../VersionBanner/index.js';

export interface SettingsDialogProps extends DialogProps {}

export const themeMapping = {
  [ThemeModeEnum.Light]: {
    icon: PiSunDimDuotone,
    label: 'Light',
  },
  [ThemeModeEnum.Dark]: {
    icon: PiMoonDuotone,
    label: 'Dark',
  },
};

export const SettingsDialog: React.FC<SettingsDialogProps> = (props) => {
  const set = useStore(({ set }) => set);
  const theme = useStore(({ theme }) => theme);
  const settings = useStore(({ settings }) => settings);
  const translate = useTranslate();

  const settingsLabelMapping: {
    [key in keyof Omit<UserSettings, 'disableCameraTravelling'>]: string;
  } = useMemo(
    () => ({
      askBeforeDeleteNodes: translate('Ask before remove a setup node'),
      askBeforeUnlinkNodes: translate('Ask before unlinking a setup node'),
      checkRestrictedAreas: translate('Ignore restricted areas'),
      enableCollisions: translate('Enable collisions'),
      // [SPZD-733] disabling camera fly through temporarily
      // disableCameraTravelling: translate('Disable camera animation when scene is loaded'),
      showGrid: translate('Show grid'),
      showButtonTitles: translate('Show menu titles'),
    }),
    [translate]
  );

  const themeNames = Object.keys(themeMapping) as (keyof typeof themeMapping)[];

  const { icon: ThemeIcon } =
    themeMapping[themeNames[themeNames.indexOf(theme) % themeNames.length]];

  // TODO this should be controlled by Planner zustand middleware
  useLayoutEffect(() => {
    document.documentElement.className = theme;
  }, [theme]);

  const handleSettingChange = useEventCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    set(({ settings }) => ({
      settings: {
        ...settings,
        [evt.target.name]: evt.target.checked,
      },
    }));
  });

  const userSettingNodes = useMemo(
    () =>
      Object.entries(settingsLabelMapping).map(([userSettingKey, userSettingLabel]) => {
        return (
          <Flex key={userSettingKey} gap="var(--spacing-gap)" justifyContent="space-between">
            <Typography>{userSettingLabel}</Typography>
            <Switch
              checked={settings[userSettingKey as keyof UserSettings]}
              name={userSettingKey}
              onChange={handleSettingChange}
            />
          </Flex>
        );
      }),
    [handleSettingChange, settings, settingsLabelMapping]
  );

  return (
    <>
      <Dialog {...props} fullWidth maxWidth="650px">
        <DialogHeader>
          <Typography variant="headline">Settings</Typography>
        </DialogHeader>
        <DialogContent>
          <Flex column gap="var(--spacing-gap)">
            <Flex column gap="var(--spacing-gap)">
              <ToggleButton
                onClick={() => {
                  set(({ theme }) => ({
                    theme: themeNames[(themeNames.indexOf(theme) + 1) % themeNames.length],
                  }));
                }}
              >
                <ThemeIcon />
                <Typography size="small" variant="label">
                  <Translate>Theme</Translate>
                </Typography>
              </ToggleButton>
            </Flex>
            <Flex column gap="var(--spacing-gap)">
              {userSettingNodes}
            </Flex>
            <UnitsSelector />
            <LanguageSelector />
          </Flex>
        </DialogContent>
        <Flex justifyContent="center">
          <VersionBanner />
        </Flex>
      </Dialog>
    </>
  );
};
